import { useState, useEffect, useRef } from 'react';
import { Board, User } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/ui/dialog';
import { Button } from '@/components/atoms/ui/button';
import { Input } from '@/components/atoms/ui/input';
import { Label } from '@/components/atoms/ui/label';
import { PaletteEditor } from '@/components/molecules/PaletteEditor';
import {
  FiLock,
  FiGlobe,
  FiTrash2,
  FiUserPlus,
  FiAlertTriangle,
} from 'react-icons/fi';
import { useToast } from '@/hooks/use-toast';
import {
  updateBoard,
  deleteBoard,
  addBoardCollaborator,
  removeBoardCollaborator,
} from '@/services/board.service';
import { searchUsersByUsername } from '@/services/user.service';
import { logButtonClick, logError } from '@/services/analytics.service';
import { COLOR_PALETTES } from '@/components/molecules/ColorPicker';

interface BoardSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: Board;
  user: User;
  onBoardUpdate: (board: Board) => void;
  onBoardDelete: () => void;
}

export const BoardSettingsDialog: React.FC<BoardSettingsDialogProps> = ({
  open,
  onOpenChange,
  board,
  user,
  onBoardUpdate,
  onBoardDelete,
}) => {
  const { toast } = useToast();
  const [palette, setPalette] = useState<string[]>(
    board.customPalette || COLOR_PALETTES[0].colors
  );
  const [isPublic, setIsPublic] = useState(board.isPublic);
  const [savingPalette, setSavingPalette] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [showPrivacyWarning, setShowPrivacyWarning] = useState(false);
  const [pendingIsPublic, setPendingIsPublic] = useState<boolean | null>(null);

  // Collaborator management
  const [newCollaboratorUsername, setNewCollaboratorUsername] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [addingCollaborator, setAddingCollaborator] = useState(false);
  const [removingCollaborator, setRemovingCollaborator] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Danger zone
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setPalette(board.customPalette || COLOR_PALETTES[0].colors);
      setIsPublic(board.isPublic);
      setNewCollaboratorUsername('');
      setSearchResults([]);
      setShowSearchResults(false);
      setDeleteConfirmName('');
      setShowDeleteConfirm(false);
      setShowPrivacyWarning(false);
      setPendingIsPublic(null);
    }
  }, [open, board]);

  // Search users as user types
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (newCollaboratorUsername.trim().length === 0) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchUsersByUsername(
          newCollaboratorUsername.trim(),
          5
        );
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [newCollaboratorUsername]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSearchResults]);

  const handleSavePalette = async () => {
    if (palette.length !== 8 || !palette.every((c) => /^#[0-9A-F]{6}$/i.test(c))) {
      toast({
        title: 'Invalid palette',
        description: 'Palette must contain exactly 8 valid hex colors',
        variant: 'destructive',
      });
      return;
    }

    setSavingPalette(true);
    try {
      const updatedBoard = await updateBoard(
        board.id,
        { customPalette: palette },
        user.uid
      );
      onBoardUpdate(updatedBoard);
      toast({
        title: 'Palette updated',
        description: 'Board palette has been updated successfully.',
      });
      logButtonClick('Save Palette', 'BoardSettingsDialog');
    } catch (error) {
      console.error('Error updating palette:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update palette';
      logError('Update Palette Error', errorMessage, 'BoardSettingsDialog');
      toast({
        title: 'Update failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSavingPalette(false);
    }
  };

  const handlePrivacyChange = (newIsPublic: boolean) => {
    if (!board.isPublic && newIsPublic) {
      // Changing from private to public - show warning
      setPendingIsPublic(newIsPublic);
      setShowPrivacyWarning(true);
    } else {
      // Changing from public to private - no warning needed
      handleSavePrivacy(newIsPublic);
    }
  };

  const handleSavePrivacy = async (newIsPublic?: boolean) => {
    const targetIsPublic = newIsPublic !== undefined ? newIsPublic : pendingIsPublic;
    if (targetIsPublic === null) return;

    setSavingPrivacy(true);
    try {
      const updatedBoard = await updateBoard(
        board.id,
        { isPublic: targetIsPublic },
        user.uid
      );
      setIsPublic(targetIsPublic);
      onBoardUpdate(updatedBoard);
      toast({
        title: 'Privacy updated',
        description: `Board is now ${targetIsPublic ? 'public' : 'private'}.`,
      });
      logButtonClick('Save Privacy', 'BoardSettingsDialog', {
        is_public: targetIsPublic,
      });
      setShowPrivacyWarning(false);
      setPendingIsPublic(null);
    } catch (error) {
      console.error('Error updating privacy:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update privacy';
      logError('Update Privacy Error', errorMessage, 'BoardSettingsDialog');
      toast({
        title: 'Update failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleAddCollaborator = async (username?: string) => {
    const usernameToAdd = username || newCollaboratorUsername.trim();
    if (!usernameToAdd) return;

    setAddingCollaborator(true);
    try {
      const updatedBoard = await addBoardCollaborator(
        board.id,
        usernameToAdd,
        user.uid
      );
      onBoardUpdate(updatedBoard);
      setNewCollaboratorUsername('');
      setSearchResults([]);
      setShowSearchResults(false);
      toast({
        title: 'Collaborator added',
        description: `${usernameToAdd} has been added as a collaborator.`,
      });
      logButtonClick('Add Collaborator', 'BoardSettingsDialog');
    } catch (error) {
      console.error('Error adding collaborator:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add collaborator';
      logError('Add Collaborator Error', errorMessage, 'BoardSettingsDialog');
      toast({
        title: 'Add failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setAddingCollaborator(false);
    }
  };

  const handleRemoveCollaborator = async (username: string) => {
    setRemovingCollaborator(username);
    try {
      const updatedBoard = await removeBoardCollaborator(
        board.id,
        username,
        user.uid
      );
      onBoardUpdate(updatedBoard);
      toast({
        title: 'Collaborator removed',
        description: `${username} has been removed from collaborators.`,
      });
      logButtonClick('Remove Collaborator', 'BoardSettingsDialog');
    } catch (error) {
      console.error('Error removing collaborator:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to remove collaborator';
      logError('Remove Collaborator Error', errorMessage, 'BoardSettingsDialog');
      toast({
        title: 'Remove failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setRemovingCollaborator(null);
    }
  };

  const handleDeleteBoard = async () => {
    if (deleteConfirmName !== board.name) {
      toast({
        title: 'Name mismatch',
        description: 'Please enter the exact board name to confirm deletion.',
        variant: 'destructive',
      });
      return;
    }

    setDeleting(true);
    try {
      await deleteBoard(board.id, user.uid);
      toast({
        title: 'Board deleted',
        description: 'The board has been permanently deleted.',
      });
      logButtonClick('Delete Board', 'BoardSettingsDialog');
      onBoardDelete();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting board:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete board';
      logError('Delete Board Error', errorMessage, 'BoardSettingsDialog');
      toast({
        title: 'Delete failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const isOwner = user.uid === board.ownerId;
  const canEdit = isOwner;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Board Settings</DialogTitle>
            <DialogDescription>{board.name}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Collaborators Section - Only for private boards and owners */}
            {!isPublic && canEdit && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Collaborators</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add users who can view and edit this private board. Maximum
                    20 collaborators.
                  </p>

                  {/* Current Collaborators List */}
                  {board.collaborators && board.collaborators.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {board.collaborators.map((username) => (
                        <div
                          key={username}
                          className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                        >
                          <span className="text-sm font-medium">{username}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCollaborator(username)}
                            disabled={removingCollaborator === username}
                            className="h-8 w-8 p-0"
                          >
                            <FiTrash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Collaborator Input with Search */}
                  <div className="relative" ref={searchInputRef}>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Label htmlFor="collaborator-username" className="sr-only">
                          Username
                        </Label>
                        <Input
                          id="collaborator-username"
                          placeholder="Search by username..."
                          value={newCollaboratorUsername}
                          onChange={(e) =>
                            setNewCollaboratorUsername(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && searchResults.length > 0) {
                              handleAddCollaborator(searchResults[0].username);
                            }
                          }}
                          disabled={addingCollaborator}
                          maxLength={20}
                        />
                        {/* Search Results Dropdown */}
                        {showSearchResults && searchResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {searchResults.map((resultUser) => (
                              <button
                                key={resultUser.uid}
                                type="button"
                                className="w-full text-left px-4 py-2 hover:bg-muted transition-colors flex items-center justify-between"
                                onClick={() => {
                                  handleAddCollaborator(resultUser.username);
                                }}
                                disabled={
                                  board.collaborators?.includes(resultUser.username) ||
                                  resultUser.uid === user.uid
                                }
                              >
                                <span className="text-sm font-medium">
                                  {resultUser.username}
                                </span>
                                {board.collaborators?.includes(resultUser.username) && (
                                  <span className="text-xs text-muted-foreground">
                                    Already added
                                  </span>
                                )}
                                {resultUser.uid === user.uid && (
                                  <span className="text-xs text-muted-foreground">
                                    Owner
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={() => handleAddCollaborator()}
                        disabled={
                          addingCollaborator ||
                          !newCollaboratorUsername.trim() ||
                          (board.collaborators?.length ?? 0) >= 20
                        }
                      >
                        <FiUserPlus className="h-4 w-4 mr-2" />
                        Add
                      </Button>
                    </div>
                    {board.collaborators && board.collaborators.length >= 20 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Maximum collaborators reached (20)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Palette Colors Section */}
            {canEdit && (
              <div className="space-y-4 pt-4 border-t border-border">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Palette Colors</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Customize the color palette for this board. Drag colors to
                    reorder them.
                  </p>
                  <PaletteEditor
                    palette={palette}
                    onPaletteChange={setPalette}
                    disabled={savingPalette}
                  />
                  <Button
                    onClick={handleSavePalette}
                    disabled={savingPalette}
                    className="mt-4 w-full"
                  >
                    {savingPalette ? 'Saving...' : 'Save Palette'}
                  </Button>
                </div>
              </div>
            )}

            {/* Privacy Settings */}
            {canEdit && (
              <div className="space-y-4 pt-4 border-t border-border">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Privacy</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Control who can view and edit this board.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={isPublic ? 'default' : 'outline'}
                      className="flex-1"
                      onClick={() => handlePrivacyChange(true)}
                      disabled={savingPrivacy || isPublic}
                    >
                      <FiGlobe className="mr-2 h-4 w-4" />
                      Public
                    </Button>
                    <Button
                      type="button"
                      variant={!isPublic ? 'default' : 'outline'}
                      className="flex-1"
                      onClick={() => handlePrivacyChange(false)}
                      disabled={savingPrivacy || !isPublic}
                    >
                      <FiLock className="mr-2 h-4 w-4" />
                      Private
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {isPublic
                      ? 'Anyone can view and contribute to this board'
                      : 'Only you and collaborators can view and edit this board'}
                  </p>
                </div>
              </div>
            )}

            {/* Danger Zone */}
            {canEdit && (
              <div className="space-y-4 pt-4 border-t border-border border-destructive/50">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-destructive">
                    Danger Zone
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Permanently delete this board. This action cannot be undone.
                  </p>
                  {!showDeleteConfirm ? (
                    <Button
                      variant="destructive"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <FiTrash2 className="mr-2 h-4 w-4" />
                      Delete Board
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-lg bg-destructive/10 border border-destructive/50 p-4">
                        <div className="flex items-start gap-2 mb-3">
                          <FiAlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                          <div>
                            <p className="font-semibold text-destructive mb-1">
                              Warning: This action cannot be undone
                            </p>
                            <p className="text-sm text-muted-foreground">
                              This will permanently delete the board and all its
                              pixels. All modifications will be lost.
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="delete-confirm">
                            Type the board name to confirm:{' '}
                            <span className="font-mono font-semibold">
                              {board.name}
                            </span>
                          </Label>
                          <Input
                            id="delete-confirm"
                            value={deleteConfirmName}
                            onChange={(e) => setDeleteConfirmName(e.target.value)}
                            placeholder={board.name}
                            disabled={deleting}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmName('');
                          }}
                          disabled={deleting}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleDeleteBoard}
                          disabled={
                            deleting || deleteConfirmName !== board.name
                          }
                          className="flex-1"
                        >
                          {deleting ? 'Deleting...' : 'Delete Board'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Warning Dialog */}
      <Dialog open={showPrivacyWarning} onOpenChange={setShowPrivacyWarning}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiAlertTriangle className="h-5 w-5 text-destructive" />
              Warning: Making Board Public
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to make this board public? Once public,
              anyone can view and make changes to the board. The collaborator
              feature will be disabled for public boards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPrivacyWarning(false);
                setPendingIsPublic(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSavePrivacy()}
              disabled={savingPrivacy}
            >
              {savingPrivacy ? 'Saving...' : 'Make Public'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

